import { useEffect, useMemo } from 'react'
import { flexRender } from '@tanstack/react-table'
import clsx from 'clsx'
import { useNavigate } from 'react-router-dom'
import { isEqual } from 'date-fns'

import { Log } from '@/types/logs'
import { Table, TableCell, TableHeader, TableRow, useTable } from '@/ui/Table'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { Loader } from '@/ui/Loader'
import { RadialBackground } from '@/ui/RadialBackground'
import { markUpdatedRecords } from '@/pages/UnfinishedRecords/helpers/markUpdatedRecords'
import { useUnfinishedRecordsColumns } from '@/pages/UnfinishedRecords/hooks/useUnfinishedRecordsColumns'
import { useWorkstationStore } from '@/modules/Workstation/store'
import { useWorkstationSelect } from '@/modules/Workstation/hooks/useWorkstationSelect'
import { useDateStore } from '@/components/DateInput'
import { ROUTES } from '@/router'

import styles from './UnfinishedRecordsTable.module.scss'
import tableStyles from '@/ui/Table/ui/Table.module.scss'
import { useGetPMUnfinishedLogs } from '@/pages/UnfinishedRecords/api/useGetPMUnfinishedLogs'
import { useOffline } from '@/components/Header/hooks/useOffline'

export const UnfinishedRecordsTable = () => {
    const { data: logs = [], isLoading, isError } = useGetPMUnfinishedLogs()
    const { workstation } = useWorkstationStore()
    const { date, setDate } = useDateStore()
    const { handleWorkstationChange } = useWorkstationSelect()
    const navigate = useNavigate()
    const isOffline = useOffline()

    const handleNavigateToLog = (log: Log) => {
        if (log.workstation !== workstation?.workstationNumber) {
            handleWorkstationChange(log.workstation)
        }

        if (!isEqual(log.timestamp, date)) {
            setDate(new Date(log.timestamp))
        }

        navigate(ROUTES.HOME)
    }

    const columns = useUnfinishedRecordsColumns(isLoading, handleNavigateToLog)

    const formattedLogs = useMemo(() => markUpdatedRecords(logs), [logs])

    const table = useTable<Log>({
        data: formattedLogs,
        getRowId: (row) => row.id,
        enableMultiRowSelection: false,
        columns: columns,
    })

    useEffect(() => {
        if (isLoading || !logs.length) {
            return
        }

        table.setSorting([
            {
                id: COMBINED_LOGS_COLUMNS.Time,
                desc: false,
            },
        ])
    }, [logs, isLoading])

    if (isLoading) {
        return <Loader />
    }

    if (!logs.length) {
        return isError || isOffline ? (
            <div className={styles.emptyContainer}>
                <RadialBackground className={styles.background} variant="main">
                    <h4 className={styles.title}>Unfinished records not available</h4>
                    <p className={styles.description}>
                        Something went wrong with retrieving the data, please try again later.
                    </p>
                </RadialBackground>
            </div>
        ) : (
            <div className={styles.emptyContainer}>
                <RadialBackground className={styles.background} variant="main">
                    <h4 className={styles.title}>No unfinished records</h4>
                    <p className={styles.description}>
                        Great work! Records that controllers need to complete will appear here.
                    </p>
                </RadialBackground>
            </div>
        )
    }

    return (
        <div className={styles.tableContainer}>
            <Table className={styles.table}>
                <TableHeader<Log> table={table} />
                <tbody className={styles.tableBody}>
                    {table.getRowModel().rows.map((row) => {
                        return (
                            <TableRow
                                key={row.original.id}
                                className={clsx({ [tableStyles.selectedRow]: row.getIsSelected() })}
                            >
                                {row.getVisibleCells().map((cell, cellIndex) => {
                                    const rendered = flexRender(cell.column.columnDef.cell, cell.getContext())

                                    return (
                                        <TableCell
                                            key={cellIndex}
                                            alignCenter={cell.column.columnDef.meta?.alignCenter}
                                            className={clsx({
                                                [styles.centeredCell]: !row.getIsSelected(),
                                            })}
                                        >
                                            {rendered}
                                        </TableCell>
                                    )
                                })}
                            </TableRow>
                        )
                    })}
                </tbody>
            </Table>
        </div>
    )
}
